import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Router } from "wouter";
import { useBrowserLocation } from "wouter/use-browser-location";

type BrowserLocation = ReturnType<typeof useBrowserLocation>;

const SharedLocationContext = createContext<BrowserLocation | null>(null);

function useSharedLocation(): BrowserLocation {
  const location = useContext(SharedLocationContext);

  if (!location) {
    throw new Error(
      "useSharedLocation must be used inside SharedLocationRouter",
    );
  }

  return location;
}

/**
 * One browser-location subscription for the whole app. By default every
 * wouter hook subscribes to history on its own, and on a browser Back/Forward
 * React can render between those listeners, so a route can see the new URL
 * before an ancestor that depends on the same URL (round address resolution)
 * has. Reading it from context keeps every route in step within one render.
 */
export function SharedLocationRouter({ children }: { children: ReactNode }) {
  const [path, navigate] = useBrowserLocation();
  const location = useMemo<BrowserLocation>(
    () => [path, navigate],
    [path, navigate],
  );

  return (
    <SharedLocationContext.Provider value={location}>
      <Router hook={useSharedLocation}>{children}</Router>
    </SharedLocationContext.Provider>
  );
}
